import { useState, FormEvent, useRef } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Modal } from '../ui/Modal';
import { Bookmark } from '../../types/api';
import { useUpdateBookmark, UpdateBookmarkParams } from '../../services/bookmarkActions';

interface BookmarkEditModalProps {
  bookmark: Bookmark;
  isOpen: boolean;
  onClose: () => void;
}

function BookmarkEditModal({ bookmark, isOpen, onClose }: BookmarkEditModalProps) {
  const [title, setTitle] = useState(bookmark.title);
  const [url, setUrl] = useState(bookmark.url);
  const [description, setDescription] = useState(bookmark.description || '');
  const [error, setError] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Private bookmarks (email-in, uploads) have an internal instapaper-private://
  // URL that isn't user-editable, so the URL field is hidden and never submitted.
  const isPrivate = Boolean(bookmark.private_source);

  const updateBookmarkMutation = useUpdateBookmark();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // clear any previous errors
    setError(null);

    const trimmedTitle = title.trim();
    const trimmedUrl = url.trim();
    const trimmedDescription = description.trim();

    if (!trimmedTitle) {
      setError(t`Title is required`);
      return;
    }

    if (trimmedTitle.length > 500) {
      setError(t`Title must be 500 characters or less`);
      return;
    }

    if (!isPrivate && !trimmedUrl) {
      setError(t`Please enter a valid URL.`);
      return;
    }

    try {
      const updateParams: UpdateBookmarkParams = {
        bookmarkId: bookmark.id,
        title: trimmedTitle,
        ...(isPrivate ? {} : { url: trimmedUrl }),
        description: trimmedDescription,
      };

      await updateBookmarkMutation.mutateAsync(updateParams);

      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t`Sorry, there was an error. Please try again.`;
      setError(errorMessage);
    }
  };

  const handleClose = () => {
    // reset form to original values when closing
    setTitle(bookmark.title);
    setUrl(bookmark.url);
    setDescription(bookmark.description || '');
    setError(null);
    onClose();
  };

  const titleLength = title.length;

  return (
    <Modal.Root
      isOpen={isOpen}
      onClose={handleClose}
      className="w-full max-w-lg"
      ariaLabel={t`Edit Article`}
      initialFocusRef={titleInputRef}
    >
      <Modal.Header title={t`Edit Article`} />
      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="bookmark-title"
              className="text-copy-primary mb-1 block text-sm font-medium"
            >
              <Trans>Title</Trans>
            </label>
            <input
              ref={titleInputRef}
              id="bookmark-title"
              type="text"
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
              maxLength={500}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={updateBookmarkMutation.isPending}
              required
              aria-describedby={error ? 'bookmark-error' : undefined}
            />
            <div className="text-copy-muted mt-1 text-xs">
              <Trans>{titleLength}/500 characters</Trans>
            </div>
          </div>

          {!isPrivate && (
            <div>
              <label
                htmlFor="bookmark-url"
                className="text-copy-primary mb-1 block text-sm font-medium"
              >
                <Trans>URL</Trans>
              </label>
              <input
                id="bookmark-url"
                type="url"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={updateBookmarkMutation.isPending}
                required
                aria-describedby={error ? 'bookmark-error' : undefined}
              />
            </div>
          )}

          <div>
            <label
              htmlFor="bookmark-description"
              className="text-copy-primary mb-1 block text-sm font-medium"
            >
              <Trans>Description (optional)</Trans>
            </label>
            <textarea
              id="bookmark-description"
              className="resize-vertical w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={updateBookmarkMutation.isPending}
              aria-describedby={error ? 'bookmark-error' : undefined}
            />
          </div>

          {error && (
            <div id="bookmark-error" className="text-danger text-sm" role="alert">
              {error}
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Actions>
          <button
            type="button"
            onClick={handleClose}
            className="text-copy-secondary rounded-md border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
            disabled={updateBookmarkMutation.isPending}
          >
            <Trans>Cancel</Trans>
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
            disabled={updateBookmarkMutation.isPending}
          >
            {updateBookmarkMutation.isPending ? (
              <Trans>Saving...</Trans>
            ) : (
              <Trans>Save changes</Trans>
            )}
          </button>
        </Modal.Actions>
      </Modal.Footer>
    </Modal.Root>
  );
}

export default BookmarkEditModal;
